import React, { useCallback, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { toast, CloseButtonProps, ToastOptions } from 'react-toastify'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import InputAdornment from '@mui/material/InputAdornment'
import { Field } from 'formik'
import { DebouncedTextField } from '@user/components/Formik'
import { useVoiceInput } from '@user/hooks/useVoiceInput'
import { VoiceInputButton } from '@user/components/VoiceInputButton'
import { useVoiceToTextEnabled } from '@user/store/tenantFeatures/hooks/useVoiceToTextEnabled'

const MAX_NOTE_LENGTH = 200

export function NoteInputField() {
  const { setFieldValue, isSubmitting } = useFormikContext<any>()
  const voiceToTextEnabled = useVoiceToTextEnabled()

  const toastOptions = useMemo<ToastOptions>(
    () => ({
      position: 'bottom-center',
      closeButton: (props: CloseButtonProps) => (
        <IconButton
          onClick={props.closeToast}
          data-test-id="knockStatusNoteToastCloseBtn"
          sx={{ color: '#FFFFFF', padding: '4px' }}
        >
          <CloseIcon sx={{ marginLeft: '15px' }} />
        </IconButton>
      ),
      hideProgressBar: true,
      icon: false,
      style: {
        padding: '12px 16px',
        backgroundColor: '#212121',
        color: '#FFFFFF',
        fontSize: '14px',
        minHeight: '40px',
        maxHeight: '40px',
        textAlign: 'center',
      },
    }),
    [],
  )

  const handleVoiceResult = useCallback(
    (transcript: string) => {
      setFieldValue('note', transcript.slice(0, MAX_NOTE_LENGTH))
    },
    [setFieldValue],
  )

  const handleMicrophonePermissionDenied = useCallback(() => {
    toast.info('Microphone permission is disabled.', toastOptions)
  }, [toastOptions])

  const {
    supported: voiceSupported,
    listening,
    start: startMic,
    stop: stopMic,
  } = useVoiceInput({
    onResult: handleVoiceResult,
    onPermissionDenied: handleMicrophonePermissionDenied,
  })

  const handleMicKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      startMic()
    }
  }

  const handleMicKeyUp = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      stopMic()
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.2,
        mt: 1.2,
      }}
    >
      <Typography variant="body1">Add Reason</Typography>
      <Field
        name="note"
        label=""
        disabled={isSubmitting}
        component={DebouncedTextField}
        multiline
        inputProps={{
          maxLength: MAX_NOTE_LENGTH,
          minRows: 2,
          maxRows: 3,
        }}
        charCountMax={MAX_NOTE_LENGTH}
        InputProps={{
          endAdornment:
            voiceToTextEnabled && voiceSupported ? (
              <InputAdornment position="end">
                <VoiceInputButton
                  listening={listening}
                  onMouseDown={startMic}
                  onMouseUp={stopMic}
                  onKeyDown={handleMicKeyDown}
                  onKeyUp={handleMicKeyUp}
                />
              </InputAdornment>
            ) : undefined,
        }}
      />
    </Box>
  )
}
